/**
 * Rating Modifier Panel
 * 
 * Additional context dropdowns for ratings:
 * - Experience: never, once, few_times, regularly
 * - Context: solo, with_partner, group, any
 * - Role: giving, receiving, both, watching
 * 
 * Per Explorer Mode spec section 2.1
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Settings2, ChevronDown, ChevronUp, Loader2, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../utils/api';

const EXPERIENCE_OPTIONS = [
    { value: '', label: 'Not specified' },
    { value: 'never', label: 'Never tried' },
    { value: 'once', label: 'Tried once' },
    { value: 'few_times', label: 'A few times' },
    { value: 'regularly', label: 'Regularly' }
];

const CONTEXT_OPTIONS = [
    { value: '', label: 'Any context' },
    { value: 'solo', label: 'Solo only' },
    { value: 'with_partner', label: 'With partner' },
    { value: 'group', label: 'Group setting' },
    { value: 'any', label: 'All contexts' }
];

const ROLE_OPTIONS = [
    { value: '', label: 'Not specified' },
    { value: 'giving', label: 'Giving / Top' },
    { value: 'receiving', label: 'Receiving / Bottom' },
    { value: 'both', label: 'Both / Versatile' },
    { value: 'watching', label: 'Watching / Voyeur' }
];

function RatingModifierPanel({
    entityType,
    entityId,
    personaId,
    initialModifiers = {},
    onModifiersChange,
    compact = false
}) {
    const { getAuthHeaders } = useAuth();

    const [expanded, setExpanded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const [modifiers, setModifiers] = useState({
        experience: initialModifiers.experience || '',
        context: initialModifiers.context || '',
        role: initialModifiers.role || '',
        notes: initialModifiers.notes || ''
    });

    // Sync with initialModifiers when they change
    useEffect(() => {
        setModifiers({
            experience: initialModifiers.experience || '',
            context: initialModifiers.context || '',
            role: initialModifiers.role || '',
            notes: initialModifiers.notes || ''
        });
    }, [initialModifiers]);

    const handleChange = async (field, value) => {
        const newModifiers = { ...modifiers, [field]: value };
        setModifiers(newModifiers);

        // Auto-save after change
        await saveModifiers(newModifiers);
    };

    const saveModifiers = async (mods) => {
        if (!personaId || !entityType || !entityId) return;

        setSaving(true);

        try {
            const headers = await getAuthHeaders();

            // Modifiers are saved as part of the rating
            const response = await fetch(`${API_BASE}/api/ratings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify({
                    persona_id: personaId,
                    entity_type: entityType,
                    entity_id: entityId,
                    // Don't change the rating, just update modifiers
                    experience: mods.experience || null,
                    context: mods.context || null,
                    role: mods.role || null,
                    notes: mods.notes || null
                })
            });

            if (response.ok) {
                setSaved(true);
                setTimeout(() => setSaved(false), 2000);

                if (onModifiersChange) {
                    onModifiersChange(mods);
                }
            }
        } catch (err) {
            console.error('Error saving modifiers:', err);
        } finally {
            setSaving(false);
        }
    };

    // Compact inline display
    if (compact) {
        return (
            <div className="flex items-center gap-2 text-xs">
                <select
                    value={modifiers.experience}
                    onChange={(e) => handleChange('experience', e.target.value)}
                    className="bg-dark-800 text-dark-300 px-2 py-1 border border-dark-700 focus:border-wine-500 outline-none"
                >
                    {EXPERIENCE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                <select
                    value={modifiers.role}
                    onChange={(e) => handleChange('role', e.target.value)}
                    className="bg-dark-800 text-dark-300 px-2 py-1 border border-dark-700 focus:border-wine-500 outline-none"
                >
                    {ROLE_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>

                {saving && <Loader2 className="w-3 h-3 animate-spin text-wine-400" />}
                {saved && <Check className="w-3 h-3 text-green-400" />}
            </div>
        );
    }

    return (
        <div className="mt-2">
            <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1*text-xs text-dark-400 hover:text-cream transition-colors"
            >
                <Settings2 size={12} />
                <span>Add details</span>
                {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                {saving && <Loader2 className="w-3 h-3 animate-spin text-wine-400 ml-1" />}
                {saved && <Check className="w-3 h-3 text-green-400 ml-1" />}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="overflow-hidden"
                    >
                        <div className="pt-3 space-y-3">
                            {/* Experience */}
                            <div>
                                <label className="block text-xs text-dark-400 mb-1">Experience</label>
                                <select
                                    value={modifiers.experience}
                                    onChange={(e) => handleChange('experience', e.target.value)}
                                    className="w-full bg-dark-800 text-cream px-2 py-1.5 text-sm border border-dark-700 focus:border-wine-500 outline-none"
                                >
                                    {EXPERIENCE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Context */}
                            <div>
                                <label className="block text-xs text-dark-400 mb-1">Preferred Context</label>
                                <select
                                    value={modifiers.context}
                                    onChange={(e) => handleChange('context', e.target.value)}
                                    className="w-full bg-dark-800 text-cream px-2 py-1.5 text-sm border border-dark-700 focus:border-wine-500 outline-none"
                                >
                                    {CONTEXT_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Role */}
                            <div>
                                <label className="block text-xs text-dark-400 mb-1">Role Preference</label>
                                <select
                                    value={modifiers.role}
                                    onChange={(e) => handleChange('role', e.target.value)}
                                    className="w-full bg-dark-800 text-cream px-2 py-1.5 text-sm border border-dark-700 focus:border-wine-500 outline-none"
                                >
                                    {ROLE_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Notes */}
                            <div>
                                <label className="block text-xs text-dark-400 mb-1">Private Notes</label>
                                <textarea
                                    value={modifiers.notes}
                                    onChange={(e) => setModifiers(prev => ({ ...prev, notes: e.target.value }))}
                                    onBlur={() => saveModifiers(modifiers)}
                                    placeholder="Add personal notes about this item..."
                                    rows={2}
                                    className="w-full bg-dark-800 text-cream px-2 py-1.5 text-sm border border-dark-700 focus:border-wine-500 outline-none resize-none placeholder:text-dark-500"
                                />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default RatingModifierPanel;
